import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import AgoraRTC, { IAgoraRTCRemoteUser, ICameraVideoTrack, IMicrophoneAudioTrack } from "agora-rtc-sdk-ng";
import VideoControls from "../interview-video/VideoControls";
import TimeWarningModal from "../interview-video/TimeWarningModal";
import "../interview-video/interview-video.css";
import { useAgoraApi } from "src/_common/hooks/actions";
import { useAuthStatus } from "src/_common/hooks/auth/authHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { MEET_EXTERNAL_URL, URLS } from "src/_config";

interface InstantMeetParams {
  meetingId: string;
}

const InstantMeet: React.FC = () => {
  const { meetingId } = useParams<InstantMeetParams>();
  const history = useHistory();
  const isAuthenticated = useAuthStatus();
  const { t } = useTranslation("settings");
  const { callGetInstantCallDetails } = useAgoraApi();
  const toast = useToaster();

  // Check if redirect=true is present in URL (coming from jobseeker end)
  const urlParams = new URLSearchParams(window.location.search);
  const shouldAutoStart = urlParams.get("redirect") === "true";

  // Core state
  const [showLoginModal, setShowLoginModal] = useState(!shouldAutoStart);
  const [isJoined, setIsJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [callEnded, setCallEnded] = useState(false);

  // Video call state
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [localVideoTrack, setLocalVideoTrack] = useState<ICameraVideoTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<IMicrophoneAudioTrack | null>(null);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  // User names from API
  const [localUserName, setLocalUserName] = useState<string>("You");
  const [remoteUserName, setRemoteUserName] = useState<string>("Participant");

  // Track if API was called when both users joined
  const [bothUsersApiCalled, setBothUsersApiCalled] = useState<boolean>(false);

  // Timer state (30 minutes countdown)
  const [remainingTime, setRemainingTime] = useState<number>(30 * 60);
  const [showTimeWarning, setShowTimeWarning] = useState<boolean>(false);
  const [timeWarningShown, setTimeWarningShown] = useState<boolean>(false);

  // Client
  const [client] = useState(() => AgoraRTC.createClient({ mode: "rtc", codec: "vp8" }));

  // Handle employer login
  const handleEmployerLogin = () => {
    if (isAuthenticated) {
      setShowLoginModal(false);
    } else {
      localStorage.setItem("postLoginRedirect", window.location.pathname);
      history.push(`${URLS.AUTH.LOGIN}`);
    }
  };

  // Handle jobseeker login
  const handleJobseekerLogin = () => {
    window.location.href = `${MEET_EXTERNAL_URL}login?intent=meet_now&channel=${encodeURIComponent(meetingId)}`;
  };

  // Check if user came back from login
  useEffect(() => {
    const rurl = urlParams.get("rurl");
    if (isAuthenticated && rurl) {
      setShowLoginModal(false);
      history.replace(window.location.pathname);
    }
  }, [isAuthenticated, history]);

  // Cleanup on unmount and fetch data
  useEffect(() => {
    if (!meetingId) return;

    if (!showLoginModal) {
      fetchMeetingData();
    }

    return () => {
      cleanupCall();
    };
  }, [meetingId, showLoginModal]);

  // Timer countdown effect
  useEffect(() => {
    if (isLoading || !isJoined || joinError) return;

    const timerInterval = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 0) return 0;
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isLoading, isJoined, joinError]);

  // Show 5-minute warning
  useEffect(() => {
    if (remainingTime === 300 && !timeWarningShown) {
      setShowTimeWarning(true);
      setTimeWarningShown(true);
    }
  }, [remainingTime, timeWarningShown]);

  // End call when time reaches 0
  useEffect(() => {
    if (remainingTime === 0 && isJoined) {
      setShowTimeWarning(false);
      toast.warning("Meeting time has ended");
      leaveCall();
    }
  }, [remainingTime, isJoined]);

  // Call API again when both users join
  useEffect(() => {
    if (isJoined && remoteUsers.length > 0 && !bothUsersApiCalled) {
      console.log("Both users are present, calling API again...");
      setBothUsersApiCalled(true);

      callGetInstantCallDetails(
        meetingId,
        (message: string, response: any) => {
          console.log("API called after both users joined:", response);
          if (response?.video_call) {
            // Update names if needed
            const { host, guest } = response.video_call;
            const meetingCreatorKey = `meeting_creator_${response.video_call.channelName}`;
            const isCreator = localStorage.getItem(meetingCreatorKey) === "true";

            if (isCreator) {
              setLocalUserName(host?.name || "You");
              setRemoteUserName(guest?.name || "Participant");
            } else {
              setLocalUserName(guest?.name || "You");
              setRemoteUserName(host?.name || "Participant");
            }
          }
        },
        (error: string) => {
          console.error("Failed to fetch meeting details after both users joined:", error);
        }
      );
    }
  }, [isJoined, remoteUsers.length, bothUsersApiCalled, meetingId, callGetInstantCallDetails]);

  // Play local video when track is available and component is mounted
  useEffect(() => {
    if (localVideoTrack && isJoined && !showLoginModal) {
      const localPlayer = document.getElementById("local-player");
      if (localPlayer) {
        try {
          localVideoTrack.play("local-player");
          console.log("Local video playing");
        } catch (error) {
          console.error("Error playing local video:", error);
        }
      }
    }
  }, [localVideoTrack, isJoined, showLoginModal]);

  const fetchMeetingData = () => {
    setIsLoading(true);

    callGetInstantCallDetails(
      meetingId,
      (message: string, response: any) => {
        if (!response?.video_call) {
          toast.error("Invalid meeting data");
          setJoinError("Invalid meeting data");
          setIsLoading(false);
          return;
        }
        initializeAgoraClient(response.video_call);
      },
      (error: string) => {
        toast.error("Failed to load meeting details");
        setJoinError("Failed to load meeting details");
        setIsLoading(false);
      }
    );
  };

  const handleUserPublished = async (user: IAgoraRTCRemoteUser, mediaType: "audio" | "video") => {
    try {
      await client.subscribe(user, mediaType);

      setRemoteUsers((prev) => {
        const exists = prev.find((u) => u.uid === user.uid);
        if (exists) {
          return prev.map((u) => (u.uid === user.uid ? user : u));
        }
        return [...prev, user];
      });

      if (mediaType === "video") {
        const player = document.getElementById(`remote-player-${user.uid}`);
        if (player && user.videoTrack) {
          user.videoTrack.play(`remote-player-${user.uid}`);
        }
      }

      if (mediaType === "audio" && user.audioTrack) {
        user.audioTrack.setVolume(100);
        user.audioTrack.play();
      }
    } catch (error) {
      console.error("Error in handleUserPublished:", error);
    }
  };

  const handleUserUnpublished = (user: IAgoraRTCRemoteUser) => {
    setRemoteUsers((prev) => prev.map((u) => (u.uid === user.uid ? user : u)));
  };

  const handleUserJoined = (user: IAgoraRTCRemoteUser, showNotification: boolean = true) => {
    setRemoteUsers((prev) => {
      const exists = prev.find((u) => u.uid === user.uid);
      if (exists) return prev;
      return [...prev, user];
    });
    if (showNotification) {
      toast.show("A participant has joined the meeting");
    }
  };

  const handleUserLeft = (user: IAgoraRTCRemoteUser) => {
    setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
    toast.show("A participant has left the meeting");
  };

  const initializeAgoraClient = async (videoCallData: any) => {
    try {
      const { appId, channelName, host, guest } = videoCallData;

      if (!appId || !channelName) {
        toast.error("Invalid video call configuration");
        setJoinError("Invalid video call configuration");
        setIsLoading(false);
        return;
      }

      const trimmedAppId = appId.trim();
      if (trimmedAppId.length === 0) {
        toast.error("Invalid Room ID");
        setJoinError("Invalid Room ID");
        setIsLoading(false);
        return;
      }

      if (!host && !guest) {
        toast.error("Invalid video call configuration");
        setJoinError("Invalid video call configuration");
        setIsLoading(false);
        return;
      }

      // Determine role
      const meetingCreatorKey = `meeting_creator_${channelName}`;
      const isCreator = localStorage.getItem(meetingCreatorKey) === "true";
      let credentials = isCreator ? host : guest;

      if (!credentials) {
        credentials = guest || host;
        console.warn("Could not determine user role, using fallback credentials");
      }

      if (!credentials || !credentials.token) {
        toast.error("Invalid authentication credentials");
        setJoinError("Invalid authentication credentials");
        setIsLoading(false);
        return;
      }

      // Set user names based on role
      if (isCreator) {
        setLocalUserName(host?.name || "You");
        setRemoteUserName(guest?.name || "Participant");
      } else {
        setLocalUserName(guest?.name || "You");
        setRemoteUserName(host?.name || "Participant");
      }

      // Set up event listeners BEFORE joining
      client.on("user-published", async (user, mediaType) => {
        if (mediaType === "audio" || mediaType === "video") {
          await handleUserPublished(user, mediaType);
        }
      });

      client.on("user-unpublished", (user, mediaType) => {
        if (mediaType === "audio" || mediaType === "video") {
          handleUserUnpublished(user);
        }
      });

      client.on("user-left", (user) => {
        handleUserLeft(user);
      });

      client.on("user-joined", (user) => {
        handleUserJoined(user);
      });

      await client.join(trimmedAppId, channelName, credentials.token, credentials.uid);

      await new Promise((resolve) => setTimeout(resolve, 100));

      // Get existing users but don't subscribe yet
      const existingUsers = client.remoteUsers;

      // Add existing users to state first
      for (const user of existingUsers) {
        handleUserJoined(user, false);
      }

      // Create local tracks
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();

      setLocalAudioTrack(audioTrack);
      setLocalVideoTrack(videoTrack);

      // Publish local tracks first
      await client.publish([audioTrack, videoTrack]);

      // Update state to show the UI (this renders the DOM elements)
      setIsJoined(true);
      setIsMediaLoaded(true);
      setIsLoading(false);

      // Play local video after a short delay to ensure DOM is ready
      setTimeout(() => {
        const localPlayer = document.getElementById("local-player");
        if (localPlayer) {
          videoTrack.play("local-player");
        } else {
          console.error("local-player element not found");
          // Retry after another delay
          setTimeout(() => {
            videoTrack.play("local-player");
          }, 500);
        }
      }, 100);

      // NOW subscribe to existing users after DOM is ready
      setTimeout(async () => {
        for (const user of existingUsers) {
          if (user.hasAudio) {
            try {
              await client.subscribe(user, "audio");
              if (user.audioTrack) {
                user.audioTrack.setVolume(100);
                user.audioTrack.play();
              }
            } catch (error) {
              console.error("Error subscribing to existing user audio:", error);
            }
          }

          if (user.hasVideo) {
            try {
              await client.subscribe(user, "video");
              if (user.videoTrack) {
                setTimeout(() => {
                  const player = document.getElementById(`remote-player-${user.uid}`);
                  if (player) {
                    user.videoTrack!.play(`remote-player-${user.uid}`);
                    console.log(`Playing remote video for user ${user.uid}`);
                  } else {
                    console.error(`remote-player-${user.uid} element not found`);
                  }
                }, 100);
              }
            } catch (error) {
              console.error("Error subscribing to existing user video:", error);
            }
          }
        }
      }, 300);

      toast.show("You joined the meeting");
    } catch (error: any) {
      let errorMessage = "Failed to join the meeting";

      if (error?.code === "INVALID_VENDOR_KEY") {
        errorMessage = "Invalid Agora App ID";
      } else if (error?.code === "INVALID_TOKEN") {
        errorMessage = "Invalid or expired token";
      } else if (error?.message) {
        errorMessage = `Failed to join`;
      }

      setJoinError(errorMessage);
      setIsLoading(false);
      setIsMediaLoaded(true);
    }
  };

  const toggleVideo = async () => {
    if (localVideoTrack) {
      await localVideoTrack.setEnabled(isVideoMuted);
      setIsVideoMuted(!isVideoMuted);
    }
  };

  const toggleAudio = async () => {
    if (localAudioTrack) {
      await localAudioTrack.setEnabled(isAudioMuted);
      localAudioTrack.setVolume(100);
      setIsAudioMuted(!isAudioMuted);
      remoteUsers.forEach((user) => {
        if (user.audioTrack) {
          user.audioTrack.setVolume(100);
        }
      });
    }
  };

  const cleanupCall = () => {
    try {
      if (localAudioTrack) {
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.close();
      }

      client.removeAllListeners();

      if (isJoined) {
        client.leave().catch(() => {});
      }
    } catch (error) {
      // Ignore errors during cleanup
    }
  };

  const leaveCall = async () => {
    try {
      setShowTimeWarning(false);

      if (localAudioTrack) {
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.close();
      }

      client.removeAllListeners();
      await client.leave().catch(() => {});

      setCallEnded(true);
    } catch (error) {
      console.error("Error leaving call:", error);
      setCallEnded(true);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="drjob-iv-main-container">
      <div className={`drjob-iv-content-wrapper ${showLoginModal ? "blurred" : ""}`}>
        {showLoginModal ? (
          <>
            <div className="drjob-iv-logo-container" style={{ textAlign: "center", marginBottom: 24 }}>
              <img src="/images/dr-logo.png" alt="Logo" style={{ height: 48 }} />
            </div>

            <div className="drjob-iv-header-section">
              <h1 className="drjob-iv-header-title">{t("instantMeet.header.title")}</h1>
              <div className="drjob-iv-remaining-time time-normal">
                <i className="fa fa-clock-o"></i>
                <span>{t("instantMeet.time.remainingTime")} 30:00</span>
              </div>
            </div>

            <div className="drjob-iv-video-grid">
              <div className="drjob-iv-video-wrapper drjob-iv-local-stream">
                <div
                  className="drjob-iv-video-element"
                  style={{ width: "100%", height: "100%", background: "#000" }}
                ></div>
                <div className="drjob-iv-stream-label">{localUserName}</div>
              </div>

              <div className="drjob-iv-video-wrapper drjob-iv-waiting-state">
                <div className="drjob-iv-waiting-content">
                  <div className="drjob-iv-waiting-icon">
                    <i className="fa fa-users"></i>
                  </div>
                  <p>{t("instantMeet.labels.waitingForParticipants")}</p>
                </div>
              </div>
            </div>

            <VideoControls
              isVideoMuted={false}
              isAudioMuted={false}
              onToggleVideo={() => {}}
              onToggleAudio={() => {}}
              onLeaveCall={() => {}}
              showRecordingButton={false}
            />
          </>
        ) : (
          <>
            <div className="drjob-iv-logo-container" style={{ textAlign: "center", marginBottom: 24 }}>
              <img src="/images/dr-logo.png" alt="Logo" style={{ height: 48 }} />
            </div>

            {isLoading ? (
              <div className="drjob-iv-header-section">
                <h1 className="drjob-iv-header-title">{t("instantMeet.header.loading")}</h1>
              </div>
            ) : callEnded ? (
              <>
                <div className="drjob-iv-header-section">
                  <h1 className="drjob-iv-header-title">{t("instantMeet.header.callEnded")}</h1>
                </div>
                <div className="drjob-iv-error-container">
                  <div className="drjob-iv-error-content">
                    <i
                      className="fa fa-phone-slash"
                      style={{ fontSize: "64px", color: "#27ae60", marginBottom: "20px" }}
                    ></i>
                    <h2 style={{ color: "white", fontSize: "1.5rem", marginBottom: "16px" }}>
                      {t("instantMeet.callEnded.title")}
                    </h2>
                    <p style={{ color: "white", fontSize: "1.1rem", marginBottom: "24px" }}>
                      {t("instantMeet.callEnded.message")}
                    </p>
                    <button
                      onClick={() => window.close()}
                      style={{
                        padding: "12px 32px",
                        fontSize: "1rem",
                        color: "white",
                        background: "#95a5a6",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      {t("instantMeet.callEnded.closeButton")}
                    </button>
                  </div>
                </div>
              </>
            ) : joinError ? (
              <>
                <div className="drjob-iv-header-section">
                  <h1 className="drjob-iv-header-title">{t("instantMeet.header.connectionFailed")}</h1>
                </div>
                <div className="drjob-iv-error-container">
                  <div className="drjob-iv-error-content">
                    <i
                      className="fa fa-exclamation-circle"
                      style={{ fontSize: "64px", color: "#e74c3c", marginBottom: "20px" }}
                    ></i>
                    <h2 style={{ color: "white", fontSize: "1.5rem", marginBottom: "16px" }}>
                      {t("instantMeet.error.title")}
                    </h2>
                    <p style={{ color: "white", fontSize: "1.1rem", marginBottom: "24px" }}>{joinError}</p>
                    <button
                      onClick={() => history.push("/interview-schedule")}
                      style={{
                        padding: "12px 32px",
                        fontSize: "1rem",
                        color: "white",
                        background: "#3498db",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      {t("instantMeet.error.returnButton")}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="drjob-iv-header-section">
                  <h1 className="drjob-iv-header-title">{t("instantMeet.header.title")}</h1>
                  <div className={`drjob-iv-remaining-time ${remainingTime <= 300 ? "time-warning" : "time-normal"}`}>
                    <i className="fa fa-clock-o"></i>
                    <span>
                      {t("instantMeet.time.remainingTime")} {formatTime(remainingTime)}
                    </span>
                  </div>
                </div>

                <div className="drjob-iv-video-grid">
                  {/* Local Video */}
                  <div className="drjob-iv-video-wrapper drjob-iv-local-stream">
                    <div id="local-player" className="drjob-iv-video-element"></div>
                    {isMediaLoaded && !localVideoTrack && !isVideoMuted && (
                      <div className="drjob-iv-video-unavailable">
                        <div className="drjob-iv-unavailable-content">
                          <i
                            className="fa fa-exclamation-triangle"
                            style={{ fontSize: "48px", marginBottom: "12px" }}
                          ></i>
                          <p>Camera not available</p>
                          <p style={{ fontSize: "0.85rem", marginTop: "8px", opacity: 0.8 }}>
                            Please check camera permissions
                          </p>
                        </div>
                      </div>
                    )}
                    {isVideoMuted && localVideoTrack && (
                      <div className="drjob-iv-video-unavailable">
                        <div className="drjob-iv-unavailable-content">
                          <i className="fa fa-video-slash" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                          <p>{t("instantMeet.labels.videoOff")}</p>
                        </div>
                      </div>
                    )}
                    <div className="drjob-iv-stream-label">
                      {localUserName}{" "}
                      {isVideoMuted && (
                        <span className="drjob-iv-muted-indicator">{t("instantMeet.labels.videoOffLabel")}</span>
                      )}
                      {isAudioMuted && (
                        <span className="drjob-iv-muted-indicator">{t("instantMeet.labels.muted")}</span>
                      )}
                    </div>
                  </div>

                  {/* Remote Videos */}
                  {remoteUsers.map((user) => (
                    <div key={user.uid} className="drjob-iv-video-wrapper drjob-iv-remote-stream">
                      <div id={`remote-player-${user.uid}`} className="drjob-iv-video-element"></div>
                      {!user.videoTrack && (
                        <div className="drjob-iv-video-unavailable">
                          <div className="drjob-iv-unavailable-content">
                            <i className="fa fa-video-slash" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                            <p>{t("instantMeet.labels.videoOff")}</p>
                          </div>
                        </div>
                      )}
                      <div className="drjob-iv-stream-label">
                        {remoteUserName}
                        {!user.videoTrack && (
                          <span className="drjob-iv-muted-indicator"> {t("instantMeet.labels.videoOffLabel")}</span>
                        )}
                        {!user.audioTrack && (
                          <span className="drjob-iv-muted-indicator"> {t("instantMeet.labels.muted")}</span>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Waiting State */}
                  {remoteUsers.length === 0 && (
                    <div className="drjob-iv-video-wrapper drjob-iv-waiting-state">
                      <div className="drjob-iv-waiting-content">
                        <div className="drjob-iv-waiting-icon">
                          <i className="fa fa-users"></i>
                        </div>
                        <p>{t("instantMeet.labels.waitingForParticipants")}</p>
                      </div>
                    </div>
                  )}
                </div>

                <VideoControls
                  isVideoMuted={isVideoMuted}
                  isAudioMuted={isAudioMuted}
                  onToggleVideo={toggleVideo}
                  onToggleAudio={toggleAudio}
                  onLeaveCall={leaveCall}
                  showRecordingButton={false}
                />
              </>
            )}
          </>
        )}

        {showTimeWarning && (
          <TimeWarningModal
            isVisible={showTimeWarning}
            onClose={() => setShowTimeWarning(false)}
            remainingSeconds={remainingTime}
          />
        )}
      </div>

      {showLoginModal && (
        <div className="instant-meet-login-modal-overlay">
          <div className="instant-meet-login-modal">
            <div className="instant-meet-modal-header">
              <h2>{t("instantMeet.modal.title")}</h2>
              <p>{t("instantMeet.modal.description")}</p>
            </div>
            <div className="instant-meet-modal-buttons">
              <button className="btn-preview" onClick={handleEmployerLogin}>
                <i className="fa fa-briefcase"></i>
                <span>{t("instantMeet.modal.employer")}</span>
              </button>
              <button className="btn-preview" onClick={handleJobseekerLogin}>
                <i className="fa fa-user"></i>
                <span>{t("instantMeet.modal.jobseeker")}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstantMeet;
